import useProductStore from '@/stores/productStore';
import { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import { BasicProduct } from '@/types';
import { adjustPriceByOrderType } from '@/utils';

export const useChangeProduct = (
  handleProductChanged: (
    products: BasicProduct[],
    callFormUpdate: boolean,
    shouldAddPendingNewProduct: boolean,
  ) => void,
  orderType: OrderTypeEnum,
) => {
  const { products, setProducts, isProductListValid, setIsProductListValid } = useProductStore();

  const handleSetProduct = (
    productList: BasicProduct[],
    shouldAddPendingNewProduct: boolean = true,
  ) => {
    handleProductChanged(productList, true, shouldAddPendingNewProduct); // prevent calling preview
    setProducts(productList);
  };

  const handleAddProduct = (newProduct: BasicProduct) => {
    handleSetProduct([...products, newProduct], false);
  };

  /** Add multiple products at once (from catalog multi-select) */
  const handleAddProducts = (newProducts: BasicProduct[]) => {
    // Filter out the empty placeholder row if it exists
    const existing = products.filter((p) => p.title || p.price || p.product_catalog_uid);
    handleSetProduct([...existing, ...newProducts], false);
  };

  const handleDeleteProduct = (index: number) => {
    const updatedProducts = products.filter((_, i) => i !== index);

    handleSetProduct(updatedProducts);

    return updatedProducts;
  };

  const handleUpdateProduct = (index: number, field: keyof BasicProduct, value: string) => {
    const updatedProducts = [...products];
    updatedProducts[index] = { ...updatedProducts[index] };

    if (field === 'is_fragile') {
      updatedProducts[index].is_fragile = value === 'true';
    } else {
      const newFieldValue =
        field === 'price' && value !== '' ? adjustPriceByOrderType(Number(value), orderType).toString() : value;
      updatedProducts[index][field] = newFieldValue;
    }

    handleSetProduct(updatedProducts);
    return updatedProducts;
  };

  /** Batch-update multiple fields on a single product in one store write */
  const handleUpdateProductBatch = (index: number, updates: Partial<Record<keyof BasicProduct, string>>) => {
    const updatedProducts = [...products];
    updatedProducts[index] = { ...updatedProducts[index] };

    for (const [field, value] of Object.entries(updates)) {
      if (field === 'is_fragile') {
        updatedProducts[index].is_fragile = value === 'true';
      } else if (field === 'price') {
        updatedProducts[index].price = value !== '' ? adjustPriceByOrderType(Number(value), orderType).toString() : '';
      } else {
        (updatedProducts[index] as any)[field] = value;
      }
    }

    handleSetProduct(updatedProducts);
    return updatedProducts;
  };

  return {
    handleAddProduct,
    handleAddProducts,
    handleDeleteProduct,
    handleUpdateProduct,
    handleUpdateProductBatch,
    setProducts,
    isProductListValid,
    setIsProductListValid,
  };
};
